import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const RoomSelection = ({ hostel, data, onUpdate, onNext, onPrev }) => {
  const [selectedRoomType, setSelectedRoomType] = useState(data.roomType || null);
  const [selectedRoommates, setSelectedRoommates] = useState(data.roommates || []);
  const [preferences, setPreferences] = useState(data.preferences || {
    floor: 'any',
    proximityToFacilities: false,
    quietArea: false,
    nearElevator: false
  });

  const handleRoomTypeSelect = (roomType) => {
    setSelectedRoomType(roomType);
    // Reset roommates when room type changes
    setSelectedRoommates([]);
  };

  const handleRoommateToggle = (roommateId) => {
    setSelectedRoommates(prev => {
      if (prev.includes(roommateId)) {
        return prev.filter(id => id !== roommateId);
      } else {
        const maxRoommates = selectedRoomType ? selectedRoomType.capacity - 1 : 0;
        if (prev.length < maxRoommates) {
          return [...prev, roommateId];
        }
        return prev;
      }
    });
  };

  const handlePreferenceChange = (preference, value) => {
    setPreferences(prev => ({
      ...prev,
      [preference]: value
    }));
  };

  const handleContinue = () => {
    if (!selectedRoomType) return;

    onUpdate({
      roomType: selectedRoomType,
      roommates: selectedRoommates,
      preferences: preferences
    });
    onNext();
  };

  // Mock roommate data - in real app this would come from API
  const availableRoommates = [
    {
      id: 1,
      name: "Sarah Johnson",
      faculty: "College of Computing and Information Sciences",
      year: "Year 3",
      profileImage: "https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80",
      interests: ["Programming", "Gaming", "Music"],
      smoking: false,
      studyHabits: "Regular study schedule"
    },
    {
      id: 2,
      name: "Michael Chen",
      faculty: "College of Engineering",
      year: "Year 2",
      profileImage: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80",
      interests: ["Engineering", "Sports", "Photography"],
      smoking: false,
      studyHabits: "Group study preferred"
    },
    {
      id: 3,
      name: "Grace Nakato",
      faculty: "College of Health Sciences",
      year: "Year 4",
      profileImage: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80",
      interests: ["Medicine", "Reading", "Volunteering"],
      smoking: false,
      studyHabits: "Quiet study environment"
    }
  ];

  const calculateTotalCost = () => {
    if (!selectedRoomType) return 0;
    return selectedRoomType.price * selectedRoommates.length;
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-2">Room Selection</h2>
        <p className="text-muted-foreground">
          Choose your preferred room type and accommodation preferences.
        </p>
      </div>

      {/* Room Types */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Available Room Types</h3>
        <div className="grid grid-cols-1 gap-4">
          {hostel.roomTypes.map((room, index) => (
            <div
              key={index}
              className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                selectedRoomType?.type === room.type
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50'
              } ${room.available === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={() => room.available > 0 && handleRoomTypeSelect(room)}
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between">
                <div className="flex items-start space-x-4">
                  <img
                    src={room.image}
                    alt={room.type}
                    className="w-20 h-16 object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-foreground">{room.type}</h4>
                      <div className="text-right">
                        <div className="text-lg font-bold text-primary">
                          UGX {room.price.toLocaleString()}
                        </div>
                        <div className="text-sm text-muted-foreground">{room.period}</div>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      {room.capacity} person{room.capacity > 1 ? 's' : ''} • {room.available} available
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {room.features.map((feature, idx) => (
                        <span key={idx} className="px-2 py-1 bg-muted text-xs rounded-full">
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="mt-4 md:mt-0 md:ml-4">
                  {selectedRoomType?.type === room.type ? (
                    <div className="flex items-center text-primary">
                      <Icon name="CheckCircle" size={20} className="mr-2" />
                      <span className="font-medium">Selected</span>
                    </div>
                  ) : room.available === 0 ? (
                    <span className="text-muted-foreground font-medium">Fully Booked</span>
                  ) : (
                    <Button variant="outline" size="sm">
                      Select Room
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Roommate Selection (for shared rooms) */}
      {selectedRoomType && selectedRoomType.capacity > 1 && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Find Roommates (Optional)</h3>
          <p className="text-muted-foreground text-sm">
            Connect with other students who might be interested in sharing this room type.
            You can select up to {selectedRoomType.capacity - 1} roommate{selectedRoomType.capacity - 1 > 1 ? 's' : ''}.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {availableRoommates.map((roommate) => (
              <div
                key={roommate.id}
                className={`border rounded-lg p-4 cursor-pointer transition-all ${
                  selectedRoommates.includes(roommate.id)
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
                onClick={() => handleRoommateToggle(roommate.id)}
              >
                <div className="flex items-center space-x-3 mb-3">
                  <img
                    src={roommate.profileImage}
                    alt={roommate.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div className="flex-1">
                    <h4 className="font-medium text-foreground">{roommate.name}</h4>
                    <p className="text-sm text-muted-foreground">{roommate.faculty}</p>
                    <p className="text-xs text-muted-foreground">{roommate.year}</p>
                  </div>
                  {selectedRoommates.includes(roommate.id) && (
                    <Icon name="CheckCircle" size={20} className="text-primary" />
                  )}
                </div>
                <div className="space-y-2">
                  <div>
                    <span className="text-xs font-medium text-muted-foreground">Interests: </span>
                    <span className="text-xs text-muted-foreground">
                      {roommate.interests.join(', ')}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-medium text-muted-foreground">Study Habits: </span>
                    <span className="text-xs text-muted-foreground">
                      {roommate.studyHabits}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {selectedRoommates.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center">
                <Icon name="Users" size={20} className="text-blue-500 mr-2" />
                <span className="text-blue-700 text-sm">
                  You've selected {selectedRoommates.length} potential roommate{selectedRoommates.length > 1 ? 's' : ''}.
                  We'll facilitate introductions after booking confirmation.
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Room Preferences */}
      {selectedRoomType && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Room Preferences (Optional)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Preferred Floor
              </label>
              <select
                value={preferences.floor}
                onChange={(e) => handlePreferenceChange('floor', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="any">Any Floor</option>
                <option value="ground">Ground Floor</option>
                <option value="middle">Middle Floors (2-4)</option>
                <option value="top">Top Floors (5+)</option>
              </select>
            </div>

            <div className="space-y-3">
              <label className="block text-sm font-medium text-foreground">
                Additional Preferences
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={preferences.proximityToFacilities}
                    onChange={(e) => handlePreferenceChange('proximityToFacilities', e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm">Close to academic facilities</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={preferences.quietArea}
                    onChange={(e) => handlePreferenceChange('quietArea', e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm">Quiet study area</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={preferences.nearElevator}
                    onChange={(e) => handlePreferenceChange('nearElevator', e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm">Near elevator/stairs</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cost Summary */}
      {selectedRoomType && (
        <div className="bg-muted/50 rounded-lg p-4">
          <h4 className="font-medium text-foreground mb-3">Cost Summary</h4>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Room Type:</span>
              <span>{selectedRoomType.type}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Base Rate:</span>
              <span>UGX {selectedRoomType.price.toLocaleString()} {selectedRoomType.period}</span>
            </div>
            {selectedRoommates.length > 0 && (
              <div className="flex justify-between text-sm">
                <span>Roommates Selected:</span>
                <span>{selectedRoommates.length}</span>
              </div>
            )}
            <div className="border-t border-border pt-2 flex justify-between font-medium">
              <span>Total Estimated Cost:</span>
              <span className="text-primary">
                UGX {calculateTotalCost().toLocaleString()} {selectedRoomType.period}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-6">
        <Button type="button" variant="outline" onClick={onPrev}>
          <Icon name="ArrowLeft" size={16} className="mr-2" />
          Previous
        </Button>
        <Button
          onClick={handleContinue}
          disabled={!selectedRoomType}
        >
          Continue to Details
          <Icon name="ArrowRight" size={16} className="ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default RoomSelection;